import {
  Box,
  Card,
  Grid,
  MenuItem,
  Select,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import ReversePagination from '../../../components/ReversePagination';
import { H3 } from '../../../components/Typography';
import FlexBox from '../../../components/flexbox/FlexBox';
import FlexRowAlign from '../../../components/flexbox/FlexRowAlign';
import SearchBar from '../SearchBar';
import BookmarkedPostsItem from './BookmarkedPostsItem';

const PostListSearchSection = ({
  postsData = [],
  title = '북마크한 게시글',
  icon = '/static/icons/best.png',
  filterItems = ['전체', '자유게시판', '질문답변', '정보공유', '후기', '모임'],
  onCardClick,
  showAIRecommendButton = false,
  ...props
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams] = useSearchParams();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const handleCardClick = item => {
    if (onCardClick) {
      onCardClick(item);
    } else {
      navigate('/naver/callmetellme/community/community-detail');
    }
  };

  // Community category context
  const categoryParamRaw = searchParams.get('category') || 'all';
  const [mainCategory, ...subParts] = categoryParamRaw.split('-');
  const subId = subParts.join('-');

  const [searchScope, setSearchScope] = useState('title_content');
  const [sortOrder, setSortOrder] = useState('latest');
  const [nicknameFilter, setNicknameFilter] = useState('all');

  const categoryMap = {
    all: { title: '전체', icon: '/static/icons/all.png' },
    best: {
      title: '베스트',
      icon: '/static/icons/best.png',
      subcategories: {
        general: '일반',
      },
    },
    free: {
      title: '자유',
      icon: '/static/icons/chatbubble.png',
      subcategories: {
        general: '일반',
        general2: '일반2',
        general3: '일반3',
      },
    },
    gossip: {
      title: '뒷담화',
      icon: '/static/icons/bamboo.png',
      subcategories: {
        leehanseol: '이한솔',
        kimchanseol: '김찬솔',
      },
    },
    info: {
      title: '꿀정보',
      icon: '/static/icons/honey.png',
      subcategories: {
        super: '개꿀정보',
        second: '2꿀정보',
      },
    },
    qna: {
      title: '질문답변',
      icon: '/static/icons/bamboo.png',
      subcategories: {
        general: '일반',
        general2: '일반2',
        general3: '일반3',
        general4: '일반4',
      },
    },
  };

  const currentCategoryData = categoryMap[mainCategory] || categoryMap.all;
  const subTitle =
    subId && currentCategoryData.subcategories
      ? currentCategoryData.subcategories[subId]
      : null;
  const currentCategory = {
    ...currentCategoryData,
    subTitle,
  };

  // Default posts data if none provided
  const defaultPostsData = [
    {
      id: 1,
      title: '안녕하세요 화이팅입니다~~',
      category: '자유게시판',
      content:
        '세부 내용입니다 세부 내용입니다세부 내용입니다세부 내용입니다세부 내용입니다 세부 내용입니다세부 내용입니다 세부 내용입니다세부 내용입니다 세부 내용입니 세부 내용입니다 세부 내용입니다세부 내용입니다세부 내용입니다세부 내용입니다 세부 내용입니다세부 내용입니다 세부 내용입니다세부 내용입니다 세부 내용입니 세부 내용입니다 세부 내용입니다세부 내용입니다세부 내용입니다세부 내용입니다 세부 내용입니다세부 내용입니다 세부 내용입니다세부 내용입니다 세부 내용입니',
      thumbnail: '/static/post-image/post-1.png',
      starCount: 0,
      likeCount: 5,
      commentCount: 12,
    },
    {
      id: 2,
      title: '안녕하세요 화이팅입니다~~',
      category: '자유게시판',
      content:
        '세부 내용입니다 세부 내용입니다세부 내용입니다세부 내용입니다세부 내용입니다 세부 내용입니다세부 내용입니다 세부 내용입니다세부 내용입니다 세부 내용입니',
      thumbnail: '/static/post-image/post-2.png',
      starCount: 0,
      likeCount: 5,
      commentCount: 12,
    },
    {
      id: 3,
      title: '썸네일이 없는 게시글입니다',
      category: '질문답변',
      content:
        '이 게시글은 썸네일이 없어서 이미지 영역이 표시되지 않습니다. 텍스트만으로 구성된 게시글의 예시입니다.',
      // thumbnail: null, // explicitly no thumbnail
      starCount: 2,
      likeCount: 8,
      commentCount: 5,
    },
    {
      id: 4,
      title: '빈 썸네일 문자열 게시글',
      category: '정보공유',
      content:
        '빈 문자열로 설정된 썸네일을 가진 게시글입니다. 이런 경우에도 이미지가 표시되지 않아야 합니다.',
      thumbnail: '', // empty string thumbnail
      starCount: 1,
      likeCount: 3,
      commentCount: 7,
    },
    {
      id: 5,
      title: '새로운 게시글 1',
      category: '후기',
      content:
        '이것은 새로 추가된 게시글입니다. 더 많은 데이터를 제공하기 위해 추가되었습니다. 사용자들이 다양한 콘텐츠를 볼 수 있도록 도와줍니다.',
      thumbnail: '/static/post-image/post-3.png',
      starCount: 3,
      likeCount: 15,
      commentCount: 8,
    },
    {
      id: 6,
      title: '새로운 게시글 2',
      category: '모임',
      content:
        '모임 관련 게시글입니다. 사람들이 함께 모여서 활동할 수 있는 기회를 제공합니다. 많은 관심과 참여를 기대합니다.',
      thumbnail: '/static/post-image/post-4.png',
      starCount: 1,
      likeCount: 7,
      commentCount: 3,
    },
    {
      id: 7,
      title: '정보 공유 게시글',
      category: '정보공유',
      content:
        '유용한 정보를 공유하는 게시글입니다. 많은 사람들이 이 정보를 활용할 수 있기를 바랍니다. 지속적인 업데이트를 통해 더 나은 정보를 제공하겠습니다.',
      thumbnail: '',
      starCount: 4,
      likeCount: 22,
      commentCount: 11,
    },
    {
      id: 8,
      title: '질문과 답변 게시글',
      category: '질문답변',
      content:
        '사용자들의 질문에 대한 답변을 제공하는 게시글입니다. 궁금한 점이 있으시면 언제든지 질문해 주세요. 빠른 답변을 드리도록 노력하겠습니다.',
      thumbnail: '/static/post-image/post-5.png',
      starCount: 2,
      likeCount: 9,
      commentCount: 6,
    },
  ];

  const dataToUse = postsData.length > 0 ? postsData : defaultPostsData;

  let padding;
  if (window.innerWidth > 1720 && !isTablet) {
    padding = '0px 0px';
  } else {
    padding = isTablet ? '0px 15px' : '0px 0px';
  }

  const StyledCard = styled(Card)(({ theme }) => ({
    width: '100%',
    padding: padding,
    backgroundColor: theme.palette.primary.lightGrey200,
    borderRadius: 0,
    border: 'none',
    [theme.breakpoints.down('sm')]: {
      '& > .MuiBox-root': {
        paddingBottom: 0,
      },
    },
  }));

  const selectStyles = {
    height: '48px',
    borderRadius: '50px',
    backgroundColor: '#f8f9fa',
    border: '1.5px solid #4285f4',
    color: '#333',
    fontSize: '14px',
    fontWeight: 500,
    minWidth: '110px',
    width: '120px',
    padding: '0px 0px',
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiSelect-select': {
      paddingLeft: '20px',
      paddingRight: '32px',
    },
    '& .MuiSvgIcon-root': {
      color: theme.palette.primary.mainBlue,
      right: '12px',
    },
  };

  return (
    <StyledCard
      sx={{
        border: 'none',
        overflow: 'visible',
        ...props.sx,
      }}
    >
      <Box
        sx={{
          width: '100%',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            backgroundColor: '#fff',
            padding: isMobile ? '18px' : '26px',
            borderRadius: '24px',
            boxShadow: '1px 4px 6px rgba(0, 0, 0, 0.05)',
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={isMobile ? 2 : 3}
          >
            <FlexRowAlign
              justifyContent="space-between"
              width="100%"
              sx={{ cursor: 'pointer' }}
            >
              <FlexRowAlign>
                {currentCategory.icon && (
                  <img
                    src={currentCategory.icon}
                    alt="icon"
                    style={{
                      width: '24px',
                      height: '24px',
                      marginRight: '8px',
                    }}
                  />
                )}
                <H3
                  style={{
                    fontSize: isMobile ? '18px' : '20px',
                    color: theme.palette.primary.mainBlue,
                  }}
                >
                  {currentCategory.title}
                  {currentCategory.subTitle && (
                    <span
                      style={{
                        color: theme.palette.text.secondary,
                        fontSize: isMobile ? '16px' : '18px',
                        fontWeight: 500,
                      }}
                    >
                      {' - '}
                      {currentCategory.subTitle}
                    </span>
                  )}
                </H3>
              </FlexRowAlign>
            </FlexRowAlign>
          </Box>
          {/* 커뮤니티 포스트 카드 */}
          <Grid container spacing={isMobile ? 3 : 3} pt={0}>
            <Grid item xs={12} mb={2}>
              {isMobile ? (
                <Box width="100%">
                  <Box mb={1.5}>
                    <SearchBar />
                  </Box>
                  <FlexBox
                    gap={1}
                    width="100%"
                    sx={{
                      overflowX: 'auto',
                      pb: 0.5,
                      '&::-webkit-scrollbar': { display: 'none' },
                      msOverflowStyle: 'none' /* IE and Edge */,
                      scrollbarWidth: 'none' /* Firefox */,
                    }}
                  >
                    <Select
                      value={searchScope}
                      onChange={e => setSearchScope(e.target.value)}
                      MenuProps={{ disableScrollLock: true }}
                      sx={{
                        ...selectStyles,
                        flex: '0 0 auto',
                        minWidth: 'auto',
                        fontSize: '12px',
                        height: '42px',
                      }}
                    >
                      <MenuItem value="title_content">제목+내용</MenuItem>
                      <MenuItem value="title">제목</MenuItem>
                      <MenuItem value="content">내용</MenuItem>
                    </Select>

                    <Select
                      value={sortOrder}
                      onChange={e => setSortOrder(e.target.value)}
                      MenuProps={{ disableScrollLock: true }}
                      sx={{
                        ...selectStyles,
                        flex: '0 0 auto',
                        minWidth: 'auto',
                        fontSize: '12px',
                        height: '42px',
                      }}
                    >
                      <MenuItem value="latest">최신순</MenuItem>
                      <MenuItem value="oldest">오래된순</MenuItem>
                      <MenuItem value="likes">좋아요순</MenuItem>
                    </Select>

                    <Select
                      value={nicknameFilter}
                      onChange={e => setNicknameFilter(e.target.value)}
                      MenuProps={{ disableScrollLock: true }}
                      sx={{
                        ...selectStyles,
                        flex: '0 0 auto',
                        minWidth: 'auto',
                        fontSize: '12px',
                        height: '42px',
                      }}
                    >
                      <MenuItem value="all">닉네임</MenuItem>
                      <MenuItem value="verified">인증회원</MenuItem>
                    </Select>
                  </FlexBox>
                </Box>
              ) : (
                <FlexBox gap={1.5} alignItems="center" flexDirection="row">
                  <Box flex={1} width="100%">
                    <SearchBar />
                  </Box>
                  <FlexBox gap={1} width="auto">
                    <Select
                      value={searchScope}
                      onChange={e => setSearchScope(e.target.value)}
                      MenuProps={{ disableScrollLock: true }}
                      sx={selectStyles}
                    >
                      <MenuItem value="title_content">제목+내용</MenuItem>
                      <MenuItem value="title">제목</MenuItem>
                      <MenuItem value="content">내용</MenuItem>
                    </Select>

                    <Select
                      value={sortOrder}
                      onChange={e => setSortOrder(e.target.value)}
                      MenuProps={{ disableScrollLock: true }}
                      sx={selectStyles}
                    >
                      <MenuItem value="latest">최신순</MenuItem>
                      <MenuItem value="oldest">오래된순</MenuItem>
                      <MenuItem value="likes">좋아요순</MenuItem>
                    </Select>

                    <Select
                      value={nicknameFilter}
                      onChange={e => setNicknameFilter(e.target.value)}
                      MenuProps={{ disableScrollLock: true }}
                      sx={selectStyles}
                    >
                      <MenuItem value="all">닉네임</MenuItem>
                      <MenuItem value="verified">인증회원</MenuItem>
                    </Select>
                  </FlexBox>
                </FlexBox>
              )}
            </Grid>

            {dataToUse.map((item, index) => (
              <BookmarkedPostsItem
                key={item.id}
                item={item}
                onClick={handleCardClick}
              />
            ))}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 2,
                width: '100%',
              }}
            >
              <ReversePagination totalItems={3} />
            </Box>
          </Grid>
        </Box>
      </Box>
    </StyledCard>
  );
};

export default PostListSearchSection;
